import { useState, useEffect } from 'react';
import { EnhancedQueueDashboard } from '@/components/EnhancedQueueDashboard';
import type { BillStage } from '@/lib/stage-utils';
import { ParserMetricsDashboard } from './processing/ParserMetricsDashboard';
import { ManualReviewQueue } from '@/components/manual-review/ManualReviewQueue';
import { OnboardingQueueView } from './onboarding/OnboardingQueueView';
import { AnomalyDetectionDashboard } from '@/components/validation/AnomalyDetectionDashboard';
import { ApprovalDashboard } from '@/components/approval/ApprovalDashboard';
import { PaymentTrackingDashboard } from '@/components/payment/PaymentTrackingDashboard';
import { ArchiveManagementDashboard } from '@/components/archive/ArchiveManagementDashboard';
import { ArchiveAnalyticsDashboard } from '@/components/archive/ArchiveAnalyticsDashboard';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { supabase } from '@/integrations/supabase/client';
import { useBillQueue } from '@/hooks/useBillQueue';

interface StageQueueViewProps {
  stage: BillStage;
}

const stageToActiveTab: Record<BillStage, string> = {
  intake: 'intake',
  extraction: 'extraction',
  processing: 'processing',
  manual_review: 'review',
  validation: 'validation',
  approval: 'approval',
  payment_ready: 'payment',
  archive: 'archive',
};

export function StageQueueView({ stage }: StageQueueViewProps) {
  const { currentWorkspace } = useWorkspace();
  const [userId, setUserId] = useState<string | undefined>();
  const { manualReviewItems, paymentReadyItems } = useBillQueue();

  // Get current user ID
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id);
    });
  }, []);

  const stageConfig = {
    intake: {
      title: 'Ingestion Queue',
      description: 'Bills that have been uploaded and are awaiting processing'
    },
    extraction: {
      title: 'Extraction Queue',
      description: 'Bills being extracted for data processing'
    },
    processing: {
      title: 'Processing Queue',
      description: 'Bills currently being parsed by AI extraction'
    },
    manual_review: {
      title: 'Manual Review Queue',
      description: 'Bills requiring human review due to low confidence scores or validation issues'
    },
    validation: {
      title: 'Validation Queue',
      description: 'Bills being validated against business rules and data quality checks'
    },
    approval: {
      title: 'Approval Queue',
      description: 'Bills pending approval from authorized users before payment'
    },
    payment_ready: {
      title: 'Payment Ready Queue',
      description: 'Bills approved and ready for payment export or processing'
    },
    archive: {
      title: 'Archive',
      description: 'Historical bills that have been completed and archived'
    }
  };

  const config = stageConfig[stage as keyof typeof stageConfig];
  
  // Render stage-specific queue view
  const renderStageContent = () => {
    switch (stage) {
      case 'archive':
        return (
          <div className="space-y-4">
            <ArchiveManagementDashboard workspaceId={currentWorkspace?.id} />
            <ArchiveAnalyticsDashboard workspaceId={currentWorkspace?.id} />
          </div>
        );
        
      case 'manual_review':
        return (
          <div className="space-y-6">
            <OnboardingQueueView />
            <ManualReviewQueue />
          </div>
        );
        
      case 'validation':
        return (
          <div className="space-y-4">
            <AnomalyDetectionDashboard workspaceId={currentWorkspace?.id || ''} />
            <EnhancedQueueDashboard activeTab={stageToActiveTab[stage]} />
          </div>
        );
        
      case 'approval':
        return <ApprovalDashboard userId={userId || ''} />;
        
      case 'payment_ready':
        return <PaymentTrackingDashboard workspaceId={currentWorkspace?.id || ''} />;
        
      case 'processing':
        return (
          <div className="space-y-4">
            <ParserMetricsDashboard />
            <EnhancedQueueDashboard activeTab={stageToActiveTab[stage]} />
          </div>
        );
        
      default:
        return <EnhancedQueueDashboard activeTab={stageToActiveTab[stage]} />;
    }
  };
  
  return (
    <div className="space-y-4">
      {config && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold">{config.title}</h3>
          <p className="text-sm text-muted-foreground">{config.description}</p>
        </div>
      )}
      
      {renderStageContent()}
    </div>
  );
}
